import { Ionicons } from '@expo/vector-icons';
import { useEffect, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-paper';

import DropdownInputField from '@/components/inputs/DropdownInputField';
import { ICAC_NPO_ID } from '@/constants/config';
import { giftRecurringOptions, giftTypes, predefinedValues } from '@/constants/options/DonationOptions';
import { USStates } from '@/constants/options/USStates';
import { useAuth } from '@/contexts/authContext';
import { DonationData, donationResolver } from '@/lib/validations/DonationSchema';
import { i18n } from '@/utils/i18n';

import BrandedChip from '../BrandedChip';
import BrandedButton from '../buttons/BrandedButton';
import InfoButton from '../buttons/InfoButton';
import CurrencyInputField from '../inputs/CurrencyInputField';
import DatePickerInputField from '../inputs/DateInputPickerField';
import RadioButtonGroup from '../inputs/RadioButtonGroup';
import RadioButtonInputField from '../inputs/RadioButtonInputField';
import SearchNpoAutoComplete from '../inputs/SearchNpoAutoComplete';
import { OptionType } from '../inputs/types';
import Text from '../Text';

interface DonationFormProps {
  initialValues: DonationData;
  isIcac?: boolean;
  loading?: boolean;
  onSubmitTap: (data: DonationData) => void;
}

const DonationForm = ({ initialValues, isIcac: isIcacProp, loading, onSubmitTap }: DonationFormProps) => {
  const { state } = useAuth();
  const [localIsIcac, setLocalIsIcac] = useState(isIcacProp ?? false);

  // const bsNpoId = remoteConfig.config?.broken_shepherds_npo_id;
  // ToDO: Implement actual bsNpoId from nuvei
  // const bsNpoId = '-1';

  // Strings
  const donationDate = i18n.t('donation.date');
  const donationType = i18n.t('donation.gift_types');
  const whereToDonatePlaceHolder = i18n.t('home.search_placeholder');
  const next = i18n.t('mix.next');
  const optOutUpdates = i18n.t('donation.opt_out_updates');

  const {
    control,
    handleSubmit,
    formState: { isValid },
    setValue,
    setError,
    reset,
    watch
  } = useForm<DonationData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: donationResolver as any,
    context: { isIcac: localIsIcac }
  });

  // ToDO: Enable BS option
  // ToDO: CHANGE EXTRA INFO FOR BROKEN SHEPHERDS OPTION
  const whereToDonateOptions: OptionType[] = useMemo(() => {
    if (initialValues?.whereToDonate && initialValues?.whereToDonate?.length > 0) {
      return [
        { label: initialValues.whereToDonate, value: initialValues.whereToDonateId }
        // {
        //   label: 'Broken Shepherds™',
        //   value: bsNpoId,
        //   disabled: true,
        //   extraInfo:
        //     'This feature is not yet available, but donations to Broken Shepherds™ will be possible in a future update.'
        // }
      ];
    } else {
      return [];
      // return [
      //   {
      //     label: 'Broken Shepherds™',
      //     value: bsNpoId,
      //     disabled: true,
      //     extraInfo:
      //       'This feature is not yet available, but donations to Broken Shepherds™ will be possible in a future update.'
      //   }
      // ];
    }
  }, [initialValues.whereToDonate, initialValues.whereToDonateId]);

  useEffect(() => {
    reset(initialValues);
    setLocalIsIcac(isIcacProp ?? false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues.whereToDonate, isIcacProp]);

  const whereToDonateIdWatch = watch('whereToDonateId');
  const valueWatch = watch('amount');
  const giftTypeWatch = watch('giftType');
  const giftRecurringOptionWatch = watch('giftRecurringOption');

  const showOptIn =
    !state.user?.onBoardingPreference?.subscribedToDonationChatRooms.hasOwnProperty(whereToDonateIdWatch);

  useEffect(() => {
    const whereToDonateSelected = whereToDonateOptions?.find((it) => it.value === whereToDonateIdWatch)?.label;
    if (whereToDonateSelected) setValue('whereToDonate', whereToDonateSelected);

    // If the selected NPO is not the ICAC one, we ensure localIsIcac is false
    if (whereToDonateIdWatch !== ICAC_NPO_ID) {
      setLocalIsIcac(false);
    }
  }, [setValue, whereToDonateIdWatch, whereToDonateOptions]);

  useEffect(() => {
    if (giftTypeWatch === 'one-time') setValue('giftRecurringOption', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftTypeWatch]);

  const onSubmit: SubmitHandler<DonationData> = async (data) => {
    try {
      onSubmitTap(data);
    } catch (error) {
      setError('root', { message: `${error}` });
    }
  };

  return (
    <View style={style.mainContainer}>
      {/* Search */}
      <Text style={style.subtitle}>{i18n.t('donation.choose_where')}</Text>
      <View style={style.searchNpoContainer}>
        <SearchNpoAutoComplete
          placeholder={whereToDonatePlaceHolder}
          leadingIcon={<Ionicons size={24} name="search" style={style.iconColor} />}
          claimed
          canReceiveDonations
          variant="rounded_with_shadow"
          predictionsScrollEnabled={false}
          onPredictionTap={({ value, label }) => {
            if (!whereToDonateOptions.find((it) => it.value === value)) {
              if (whereToDonateOptions.length === 1) {
                whereToDonateOptions.unshift({ label: label, value: value });
              } else {
                whereToDonateOptions[0] = { label: label, value: value };
              }
            }
            setValue('whereToDonate', label);
            setValue('whereToDonateId', value);
          }}
        />
      </View>

      {/* Where To Donate */}
      {whereToDonateOptions.length !== 0 && (
        <>
          <Divider />

          <RadioButtonGroup
            control={control}
            name="whereToDonateId"
            options={whereToDonateOptions}
            showNoneOption={false}
          />
        </>
      )}

      {localIsIcac && (
        <View style={style.icacSubtitleContainer}>
          <Text style={style.icacSubtitle} fontWeight="700">
            {i18n.t('donation.icac_info_title')}
          </Text>

          <InfoButton
            title={i18n.t('donation.icac_info_title')}
            message={i18n.t('donation.icac_info_subtitle')}
            type="modal"
          />
        </View>
      )}

      <Divider />

      {/* Amount */}
      <CurrencyInputField control={control} name="amount" />
      <View style={style.currencyOptions}>
        {predefinedValues.map((value, index) => {
          return (
            <BrandedChip
              key={index}
              isSelected={value === valueWatch}
              value={`$${value}`}
              onPress={() => setValue('amount', value)}
            />
          );
        })}
      </View>

      <Divider />

      {/* Gifts */}
      <Text fontWeight="700" style={style.giftTypes}>
        {donationType}
      </Text>
      <View style={style.currencyOptions}>
        {giftTypes.map((option, index) => {
          return (
            <BrandedChip
              key={index}
              isSelected={option.value === giftTypeWatch}
              value={option.label}
              onPress={() => {
                setValue('giftType', giftTypeWatch === option.value ? '' : option.value);
              }}
              fontSize={14}
              borderRadius={10}
            />
          );
        })}
      </View>
      {giftTypeWatch === 'recurring' && (
        <>
          <View style={style.recurringOptions}>
            {giftRecurringOptions.map((option, index) => {
              return (
                <BrandedChip
                  key={index}
                  isSelected={option.value === giftRecurringOptionWatch}
                  value={option.label}
                  onPress={() => setValue('giftRecurringOption', option.value)}
                  fontSize={12}
                  borderRadius={5}
                />
              );
            })}
          </View>

          {/* Donation Date */}
          <Text fontWeight="700" style={style.donationDate}>
            {donationDate}
          </Text>
          <DatePickerInputField
            control={control}
            name="date"
            variant="default"
            timePicker={false}
            minDate={new Date().setHours(0, 0, 0, 0)}
          />
        </>
      )}

      {localIsIcac && (
        <View style={style.stateSelectorContainer}>
          <DropdownInputField
            label={i18n.t('donation.select_state')}
            placeholder={i18n.t('donation.state_placeholder')}
            control={control}
            name="notes"
            options={USStates}
            variant="rounded_with_shadow"
          />
        </View>
      )}

      {/* Opt Monthly Update */}
      {showOptIn && (
        <>
          <Divider />
          <RadioButtonInputField label={optOutUpdates} control={control} name="optMonthlyUpdates" />
        </>
      )}

      <BrandedButton loading={loading} disabled={!isValid} onPress={handleSubmit(onSubmit)}>
        {next}
      </BrandedButton>
    </View>
  );
};

const style = StyleSheet.create({
  mainContainer: {
    flex: 1,
    gap: 25
  },

  title: {
    fontSize: 22,
    color: 'black'
  },

  subtitle: {
    color: 'black',
    opacity: 0.6
  },

  currencyOptions: {
    flexDirection: 'row',
    gap: 10,
    justifyContent: 'center'
  },

  recurringOptions: {
    flexDirection: 'row',
    gap: 5,
    justifyContent: 'center'
  },

  giftTypes: {
    fontSize: 22,
    color: 'black'
  },

  donationDate: {
    fontSize: 22,
    color: 'black'
  },

  iconColor: {
    color: '#808080'
  },

  searchNpoContainer: {
    height: 38,
    zIndex: 1000
  },

  icacSubtitle: {
    fontSize: 16,
    color: 'black',
    opacity: 0.8
  },

  icacSubtitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginTop: 5
  },

  stateSelectorContainer: {
    marginTop: 10,
    zIndex: 999
  }
});

export default DonationForm;
